import React, { forwardRef, useContext, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";

import { format } from "date-fns";
import { fi } from "date-fns/locale";

import {
  ECustomerInfo,
  EOrderDetails,
} from "./components/LanguagePack/translatedFields";
import { titleStyle } from "./components/styles";
import ContractCustomerInformation from "./components/contract-customer-information";
import ContractDriverInformation from "./components/contract-driver-information";
import ContractCompanyCustomerInformation from "./components/contract-company-customer-information";
import ContractAdditionalDriverInformation from "./components/contract-additional-driver-information";
import ContractProduct from "./components/contract-product-information";
import { ContractVehicleDamageMap } from "./components/contract-vehicle-damage-map";
import ContractPostDebit from "./components/contract-post-debit";
import ContractItemsBreakdown from "./components/contract-items-breakdown";
import ContractAgreement from "./components/contract-agreement";
import { ContractAdditionalAgreement } from "./components/contract-additional-information-fields";
import ProductConditionAgreement from "./components/contract-product-condition-agreement";
import { AppContext } from "../../contextProviders/AppContext";
import { defaultContractSettings } from "./components/initial-contract-settings";

export const contractFontSize = "12px";

export const ContractTemplatePreview = forwardRef(
  (
    {
      userEntity,
      selectedCompany,
      cart,
      setContractSettings,
      values,
      companyContractSettings,
    },
    ref
  ) => {
    const contractSettings = getContractSettings(values, selectedCompany);
    const { language, selectedAdditionalServices } = useContext(AppContext);
    const selectedLanguage = getFixedLanguage(language);

    const reservation = {
      ...values,
      reservationVehicles: getReservationVehicles(values, cart),
      additionalServices: selectedAdditionalServices,
    };
    const shortId = reservation?.shortId;
    const pageHeight = 1122;
    // const { language: selectedLanguage } = useContext(AppContext);
    const [
      contractProductsAlternativePosition,
      setContractProductsAlternativePosition,
    ] = React.useState(false);

    useEffect(() => {
      if (contractSettings?.showProductInformation) {
        if (ref?.current?.scrollHeight > pageHeight) {
          setContractProductsAlternativePosition(true);
        }

        if (ref?.current?.scrollHeight < pageHeight) {
          setContractProductsAlternativePosition(false);
        }
      }
    }, [contractSettings?.showProductInformation, ref]);

    return (
      <Stack
        sx={{
          width: "210mm",
          paddingX: "10mm",

          boxSizing: "border-box",
          backgroundColor: "white",
          margin: "auto",
          pageBreakAfter: "always",
          breakAfter: "always",
        }}
        justifyContent={"space-between"}
        spacing={1}
        ref={ref}
      >
        <Box
          sx={{
            paddingBottom: "2mm",
          }}
        >
          <Stack
            direction={"column"}
            justifyContent={"space-between"}
            height={"100%"}
            overflow={"auto"}
          >
            <Box sx={{}}>
              <Stack
                direction={"row"}
                justifyContent={"space-evenly"}
                alignItems={"center"}
                height={"50px"}
              >
                <Logo userEntity={userEntity} />
                <Typography align="center" sx={{ fontWeight: 550 }}>
                  {EOrderDetails[selectedLanguage]?.CONTRACT_TITLE}
                </Typography>
                <Logo userEntity={userEntity} />
              </Stack>

              <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack
                  direction={"row"}
                  justifyContent={shortId ? "space-between" : "flex-end"}
                  flex={1}
                  height={"18px"}
                >
                  {shortId && (
                    <Typography
                      sx={{
                        fontSize: contractFontSize,
                      }}
                    >
                      {EOrderDetails[selectedLanguage].RESERVATION_NUMBER}{" "}
                      <>{shortId}</>
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: contractFontSize,
                    }}
                  >
                    {format(new Date(), "dd.MM.yyyy", { locale: fi })}
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                sx={{
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
                direction={"row"}
              >
                <ServiceProviderCompanyInfo reservation={reservation} />
              </Stack>
              <Stack spacing={1}>
                <Typography sx={titleStyle}>
                  {ECustomerInfo[selectedLanguage].TITLE}
                </Typography>

                <Stack direction={"row"} spacing={1}>
                  <ContractCustomerInformation
                    selectedLanguage={selectedLanguage}
                    reservation={reservation}
                  />

                  <ContractDriverInformation
                    visibility={!!contractSettings.showDriverFields}
                    selectedLanguage={selectedLanguage}
                    reservation={reservation}
                  />
                </Stack>

                <ContractCompanyCustomerInformation
                  visibility={!!contractSettings.showCompanyCustomerFields} //reservation?.isCompany
                  selectedLanguage={selectedLanguage}
                  reservation={reservation}
                />
                <ContractAdditionalDriverInformation
                  visibility={!!contractSettings?.showAdditionalDriver}
                  selectedLanguage={selectedLanguage}
                  contractSettings={contractSettings}
                  reservation={reservation}
                />
                {contractSettings?.showProductInformation &&
                  !contractProductsAlternativePosition && (
                    <Stack display={"flex"}>
                      <ContractProducts
                        selectedLanguage={selectedLanguage}
                        contractSettings={contractSettings}
                        reservation={reservation}
                      />
                    </Stack>
                  )}
              </Stack>
              <ContractPostDebit
                selectedLanguage={selectedLanguage}
                contractSettings={contractSettings}
                reservation={reservation}
              />
              <ContractItemsBreakdown
                selectedLanguage={selectedLanguage}
                contractSettings={contractSettings}
                reservation={reservation}
              />
              {/* 
       Disabled due to missing data
       <ContractPaymentStatus
          contractSettings={contractSettings}
          selectedLanguage={selectedLanguage}
        /> */}
            </Box>
            <Box>
              <Stack spacing={1} justifyContent={"flex-end"}>
                <ContractAgreement
                  selectedLanguage={selectedLanguage}
                  contractSettings={contractSettings}
                  reservation={reservation}
                />
                <ContractAdditionalAgreement
                  contractSettings={contractSettings}
                  setContractSettings={setContractSettings}
                  selectedLanguage={selectedLanguage}
                  reservation={reservation}
                />
              </Stack>
            </Box>
          </Stack>
        </Box>
        {contractSettings?.showProductInformation &&
          contractProductsAlternativePosition && (
            <Stack sx={{ breakBefore: "always" }} spacing={1}>
              <ContractProducts
                selectedLanguage={selectedLanguage}
                contractSettings={contractSettings}
                reservation={reservation}
              />
              <ProductConditionAgreement
                selectedLanguage={selectedLanguage}
                reservation={reservation}
              />
            </Stack>
          )}
      </Stack>
    );
  }
);

function getContractSettings(reservation, selectedCompany) {
  const companyContractSettings = JSON.parse(
    selectedCompany?.contractSettingsJSON
  );
  return {
    ...defaultContractSettings,
    ...companyContractSettings,
  };
}

function ContractProducts({ selectedLanguage, contractSettings, reservation }) {
  //@ts-ignore
  const products = reservation?.reservationVehicles;

  return products
    ? products?.map((item) => (
        <Stack key={item.id} direction={"row"}>
          <ContractProduct
            item={item}
            selectedLanguage={selectedLanguage}
            contractSettings={contractSettings}
          />
          {contractSettings.showVehicleDamagesMap && (
            <>
              <ContractVehicleDamageMap
                vehicle={item}
                selectedLanguage={selectedLanguage}
                contractSettings={contractSettings}
              />
            </>
          )}
        </Stack>
      ))
    : null;
}

function Logo({ userEntity }) {
  const logo = null; //clientLogoData.find((item) => item.id === userEntity)?.logo;
  if (!logo) {
    return <Box />;
  }
  return (
    <img alt={"logo"} src={logo} style={{ width: "250px", margin: "10px" }} />
  );
}

const ServiceProviderCompanyInfo = () => {
  const { company } = useContext(AppContext);

  if (!company) return <Box flex={0.5} />;
  return (
    <Stack flex={0.5}>
      <Typography sx={{ fontWeight: 550, fontSize: contractFontSize }}>
        {company.name}
      </Typography>
      <Typography sx={{ fontSize: contractFontSize }}>
        {company.address} {company.postalCode} {company.city}
      </Typography>
      <Typography sx={{ fontSize: contractFontSize }}>
        {company.phone}
      </Typography>
      <Typography sx={{ fontSize: contractFontSize }}>
        {company.businessId}
      </Typography>
    </Stack>
  );
};

function getReservationVehicles(reservation, cart) {
  if (reservation?.vehicles) {
    return reservation?.vehicles;
  }
  if (cart?.length > 0) {
    const itemsInCart = cart.reduce((acc, item) => {
      if (item?.vehicle) {
        return [
          ...acc,
          { ...item.vehicle, price: item.offerPrice, tax: item.tax },
        ];
      }
      return acc;
    }, []);

    return itemsInCart;
  }
  return [];
}

function getFixedLanguage(language) {
  if (language === "fi") {
    return "FIN";
  }
  if (language === "en") {
    return "ENG";
  }
  if (language === "sv" || language === "se") {
    return "SWE";
  }
  return "FIN";
}
